/**
 * @copyright Copyright 2024 Epic Systems Corporation
 * @file banner for old app versions
 * @module Epic.AppOrchard.DownloadApp.NewerVersionBanner
 */

import { Alert, AlertDescription, AlertIcon, AlertTitle, Box } from "@chakra-ui/react";
import { AOLink } from "ao/components/Core";
import { useSiteInfoState } from "ao/state/siteInfo";
import { IShowroomAppDownloadModel } from "ao/types/showroom";
import React, { FunctionComponent, memo } from "react";

interface IProps {
	appDetails: IShowroomAppDownloadModel;
}

export const NewerVersionBanner: FunctionComponent<IProps> = memo((props) => {
	const { appDetails } = props;

	const appVersions = appDetails.appVersions;
	const newestVersion = appVersions[appVersions.length - 1];

	const { siteInfo } = useSiteInfoState((selectors) => selectors.getState(), []);

	if (appDetails.appModel.id >= newestVersion.id) {
		return null;
	} else {
		return (
			<Alert status="warning" variant="subtle">
				<AlertIcon />

				<Box>
					<AlertTitle>New Version Available</AlertTitle>
					<AlertDescription>
						<AOLink
							color="gray.800"
							url={
								newestVersion.isInShowroom
									? `${siteInfo.showroomUrl}listing?appId=${newestVersion.id}`
									: `/Download?id=${newestVersion.id}`
							}
						>
							This is an older version of the app. Click here to view the newest version.
							Contact the app's developer or use the app website URL to learn more.
						</AOLink>
					</AlertDescription>
				</Box>
			</Alert>
		);
	}
});
